import { memo, useMemo } from "react";
import Html from "react-pdf-html";

import * as cheerio from "cheerio";

import PdfDocumentTable, {
  buildTableDescriptionFromHtml
} from "components/pdf-generation-v3/components/pdf-document-html/pdf-document-table.component";
import {
  FormSettings,
  formContentWidthPx
} from "components/pdf-generation-v3/form-print.utils";
import {
  DEFAULT_PDF_CSS_STYLE,
  FORM_PDF_CSS_STYLE,
  normalizePrintableHtml
} from "components/pdf-generation-v3/pdf-generation-v3.utils";

type PdfDocumentHtmlProps = {
  bodyHtml?: string | null;
  printableTableLayoutFixesEnabled?: boolean;
  formSettings?: FormSettings | null;
};

type HtmlChunk = { kind: "table" | "html"; html: string };

// Pre-compute static HTML wrapper parts for better performance
const WRAP_PREFIX = `<html><body><style>`;
const WRAP_MID = `</style>`;
const WRAP_SUFFIX = `</body></html>`;
const DEFAULT_STYLE = DEFAULT_PDF_CSS_STYLE;
const STATIC_WRAPPER_START = `${WRAP_PREFIX}${DEFAULT_STYLE}${WRAP_MID}`;
const STATIC_WRAPPER_END = WRAP_SUFFIX;
const FORM_WRAPPER_START = `${WRAP_PREFIX}${FORM_PDF_CSS_STYLE}${WRAP_MID}`;

// The width Enhanced Table Rendering lays a table out across on every page that is not a Form.
const LEGACY_TABLE_CONTAINER_WIDTH_PX = 430;

const CONTAINER_ID = "__pdf_container__";

// The tags of the elements a table sits inside, outermost first: `open` reopens them and `close`
// closes them again, innermost first.
type TableWrappers = { open: string; close: string };

const tableWrappers = ($: cheerio.Root, table: cheerio.Element) => {
  const wrappers: TableWrappers = { open: "", close: "" };
  $(table)
    .parentsUntil(`#${CONTAINER_ID}`)
    .each((_: number, ancestor: cheerio.Element) => {
      const emptied = $.html($(ancestor).clone().empty());
      wrappers.open = `${emptied.slice(0, emptied.lastIndexOf("</"))}${wrappers.open}`;
      wrappers.close = `${wrappers.close}</${ancestor.tagName}>`;
    });
  return wrappers;
};

function splitHtmlByTables(html: string, formMode: boolean): HtmlChunk[] {
  const containerWrapped = `<div id="${CONTAINER_ID}">${html}</div>`;
  const loadFn = cheerio.load as any;
  const $ = loadFn(containerWrapped);

  const PLACEHOLDER = "__PDF_TABLE_PLACEHOLDER__";
  const tableHtmls: string[] = [];
  // ENG-2277: for a Form, what each table sits inside (a table index to its wrappers).
  const wrappersOfTable: TableWrappers[] = [];

  // Collect table HTML and replace with placeholders in one pass
  $("table").each((_: number, el: cheerio.Element) => {
    const tableHtml = $.html(el) || "";
    tableHtmls.push(tableHtml);
    if (formMode) wrappersOfTable.push(tableWrappers($, el));
    $(el).replaceWith(`<div>${PLACEHOLDER}${tableHtmls.length - 1}</div>`);
  });

  const flattened = $(`#${CONTAINER_ID}`).html() || "";

  // Early return if no tables found
  if (tableHtmls.length === 0) {
    return flattened.trim().length > 0
      ? [{ kind: "html", html: flattened }]
      : [];
  }

  const chunks: HtmlChunk[] = [];
  let cursor = 0;

  // ENG-2277: in a Form the match takes the placeholder's own <div> with it. Matching the marker alone
  // cuts each chunk inside that div, and react-pdf-html drops the stylesheet's body rules for a chunk
  // that ends in an unclosed tag. A Form takes its font from body, so the text before every table would
  // print in react-pdf's default Helvetica. Other templates keep the marker-only match, so their printing
  // does not change: their stylesheet names the font on every p and div.
  const placeholderRegex = formMode
    ? new RegExp(`<div>${PLACEHOLDER}(\\d+)</div>`, "g")
    : new RegExp(`${PLACEHOLDER}(\\d+)`, "g");
  let match;
  // ENG-2277: a Form's table can sit inside other elements (an indented block, a list item). Cutting
  // around it alone would leave those open at the end of the chunk before it and closed with nothing
  // to close in the chunk after, and an unclosed tag is what loses a chunk the Form's font (above).
  // So the chunk before a table closes what the table sits inside, and the chunk after opens it
  // again. Empty for other templates, and for a Form's table that sits inside nothing.
  let reopen = "";

  // Reset regex lastIndex for consistent behavior
  placeholderRegex.lastIndex = 0;

  while ((match = placeholderRegex.exec(flattened)) !== null) {
    const matchStart = match.index;
    const matchEnd = placeholderRegex.lastIndex;
    const tableIndex = parseInt(match[1], 10);
    const wrappers = wrappersOfTable[tableIndex];

    // Add HTML content before this table placeholder
    if (matchStart > cursor) {
      const beforeHtml = flattened.slice(cursor, matchStart);
      if (beforeHtml.trim().length > 0) {
        chunks.push({
          kind: "html",
          html: `${reopen}${beforeHtml}${wrappers?.close ?? ""}`
        });
      }
    }

    // Add the table
    const tableHtml = tableHtmls[tableIndex];
    if (tableHtml) {
      chunks.push({ kind: "table", html: tableHtml });
    }

    cursor = matchEnd;
    reopen = wrappers?.open ?? "";
  }

  // Add any remaining HTML content
  if (cursor < flattened.length) {
    const rest = flattened.slice(cursor);
    if (rest.trim().length > 0) {
      chunks.push({ kind: "html", html: `${reopen}${rest}` });
    }
  }

  return chunks;
}

function PdfDocumentHtmlWithTableAdjustments({
  bodyHtml,
  printableTableLayoutFixesEnabled,
  formSettings
}: PdfDocumentHtmlProps) {
  const chunks = useMemo(() => {
    if (!bodyHtml) return [] as HtmlChunk[];
    const normalized = normalizePrintableHtml(bodyHtml, {
      enableTableLayoutFixes: printableTableLayoutFixesEnabled,
      stripRowspanCellHeights: printableTableLayoutFixesEnabled,
      preserveBlankLines: true,
      formSettings
    });
    return splitHtmlByTables(normalized, Boolean(formSettings));
  }, [bodyHtml, printableTableLayoutFixesEnabled, formSettings]);

  const containerWidthPx = formSettings
    ? formContentWidthPx(formSettings)
    : LEGACY_TABLE_CONTAINER_WIDTH_PX;
  const wrapperStart = formSettings ? FORM_WRAPPER_START : STATIC_WRAPPER_START;

  if (!bodyHtml || chunks.length === 0) {
    const content = bodyHtml
      ? `${wrapperStart}${normalizePrintableHtml(bodyHtml, {
          enableTableLayoutFixes: printableTableLayoutFixesEnabled,
          stripRowspanCellHeights: printableTableLayoutFixesEnabled,
          preserveBlankLines: true,
          formSettings
        })}${STATIC_WRAPPER_END}`
      : "";

    return <Html>{content}</Html>;
  }

  return (
    <>
      {chunks.map((chunk, idx) => {
        if (chunk.kind === "table") {
          const desc = buildTableDescriptionFromHtml(chunk.html, {
            containerWidthPx,
            formMode: Boolean(formSettings)
          });
          if (!desc) return null;
          return <PdfDocumentTable key={idx} description={desc} />;
        }
        const wrapped = `${wrapperStart}${chunk.html}${STATIC_WRAPPER_END}`;
        return <Html key={idx}>{wrapped}</Html>;
      })}
    </>
  );
}

// Memoize component to prevent unnecessary re-renders
export default memo(PdfDocumentHtmlWithTableAdjustments);
